import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

const OutputDisplayNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const displayFormat = data.config?.displayFormat || 'json';
  const showTimestamp = data.config?.showTimestamp || false;

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-emerald-400 to-emerald-500 border-2 transition-all duration-200 ${
      selected ? 'border-emerald-600 shadow-xl scale-105' : 'border-emerald-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <DocumentTextIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-emerald-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          <div className="text-xs text-emerald-100 mt-1">
            Kimenet megjelenítése ({displayFormat.toUpperCase()})
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      {data.config && (
        <div className="mt-2 text-xs text-emerald-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-emerald-200 rounded-full" />
              <span className="truncate">
                Formátum: {displayFormat}
              </span>
            </div>
            {showTimestamp && (
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-emerald-200 rounded-full" />
                <span className="truncate">
                  Időbélyeg: ✓
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result - Display output */}
      {data.executionData && (
        <div className="mt-2 text-xs bg-black bg-opacity-30 px-2 py-2 rounded max-h-64 overflow-y-auto">
          <div className="text-emerald-100 font-semibold mb-1 flex items-center justify-between">
            <span>📊 Kimenet:</span>
            {data.executionData.display_format && (
              <span className="text-emerald-300 text-xs">
                {data.executionData.display_format.toUpperCase()}
              </span>
            )}
          </div>
          <pre className="text-white whitespace-pre-wrap break-words font-mono text-xs bg-gray-900 bg-opacity-50 p-2 rounded">
            {data.executionData.formatted_output 
              ? data.executionData.formatted_output
              : displayFormat === 'json' 
                ? JSON.stringify(data.executionData.output || data.executionData, null, 2)
                : String(data.executionData.output || data.executionData)
            }
          </pre>
          {showTimestamp && data.executionData.timestamp && (
            <div className="text-emerald-200 text-xs mt-2 pt-1 border-t border-emerald-500 border-opacity-30">
              ⏱️ {new Date(data.executionData.timestamp).toLocaleString('hu-HU')}
            </div>
          )}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="w-3 h-3 bg-white border-2 border-emerald-500"
        style={{ left: -6 }}
      />

      {/* No output handle - this is a terminal node */}
    </div>
  );
};

export default OutputDisplayNode;
